import { FC, useState } from 'react'
import cn from 'classnames'
import { Button, Grid, Link } from 'components/ui'
import { emptyImagePlaceholder } from 'constants/image'
import { ROUTE_PRODUCT, ROUTE_PRODUCT_EXP } from 'routes/products'
import { ProductPrice, ProductQuantity } from 'modules/products/components'
import { BrowserView, MobileOnlyView } from 'react-device-detect'
import { Day } from 'components/icons'
import s from './product-card.module.css'
import type { ProductCardProps } from '.'
import { ProductBuy } from '..'
import { useRouter } from 'next/router'
import { isPedidoFacilSession } from 'modules/pedido-facil/utils/pedido-facil-session'
import {useMercadinho} from "hooks/use-mercadinho";
import { useAfiliado } from 'hooks/use-afiliado'
import AffiliateService from 'modules/affiliates/services/affiliate-service'
import { Product } from 'framework/products'

// Adicionar isAfiliadoFavorite como opcional ao tipo Product

declare module 'framework/products' {
  interface Product {
    isAfiliadoFavorite?: boolean;
  }
}

const StarFilled = (props: any) => (
  <svg width="24" id="creditos-honrosos-julia-bellusci" height="24" viewBox="0 0 24 24" fill="#FFD700" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
)
const StarOutline = (props: any) => (
  <svg width="24" id="creditos-honrosos-julia-bellusci" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
)

const ProductCard: FC<ProductCardProps> = ({ className, product, imgProps, ...props }) => {
  const [quantity, setQuantity] = useState<number>(1)
  const router = useRouter()
  const { isMercadinho } = useMercadinho()
  const { isAfiliado } = useAfiliado()
  const inPedidoFacilSession = isPedidoFacilSession(router.pathname)
  const [isFavorite, setIsFavorite] = useState(product.isAfiliadoFavorite)
  const [loadingFavorite, setLoadingFavorite] = useState(false)
  const productSlug = product.slug || ''

  const afiliadoIdQueryParm = product.afiliadoId ? `?afiliadoId=${product.afiliadoId}` : ''
  const linkProps = {
    href: `${ROUTE_PRODUCT}${productSlug}${afiliadoIdQueryParm}`,
    passHref: true,
    prefetch: true,
    ...props,
  }

  function onQuantityChange({ next }: { next: number }) {
    setQuantity(next)
  }

  const freezeProduct = product
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { image, images, name, price, stock, days, tipo_produto } = product

  // eslint-disable-next-line no-nested-ternary
  const imagesSet = !image || image === '' ? (images.length > 0 ? images[0].url : null) : image

  const expLinkProps = {
    ...linkProps,
    href: `${ROUTE_PRODUCT_EXP}${productSlug}`,
  }

  const url = tipo_produto === 'convencional' ? linkProps : expLinkProps

  if (isMercadinho) {
    url.href = '#'
  }

  if (inPedidoFacilSession) {
    url.href = '#'
  }

  // Favoritar/desfavoritar produto afiliado
  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (loadingFavorite) return
    setLoadingFavorite(true)
    try {
      const affiliateService = new AffiliateService()
      if (isFavorite) {
        // Remover favorito
        await affiliateService.setUrl(`/afiliados/delete-produto-favorito?produto_id=${product.id}`).delete()
        setIsFavorite(false)
      } else {
        // Adicionar favorito
        await affiliateService.setUrl(`/afiliados/add-produto-favorito`).post({ produto_id: product.id })
        setIsFavorite(true)
      }
    } catch (err) {
      // Pode exibir um toast de erro se desejar
    } finally {
      setLoadingFavorite(false)
    }
  }

  return (
    <div className={cn(s.root, stock <= 0 && s.unavailable, className)}>
      {isAfiliado && (
        <div
          className={cn(s.favoriteStar, !isFavorite && s['favoriteStar--empty'])}
          style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, cursor: loadingFavorite ? 'not-allowed' : 'pointer' }}
          onClick={handleToggleFavorite}
          title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        >
          {isFavorite ? <StarFilled /> : <StarOutline />}
        </div>
      )}
      <Link {...url} className={s.image}>
        <div className={s.image}>
          <img alt={name || ''} src={imagesSet || emptyImagePlaceholder} {...imgProps} />
        </div>
      </Link>
      <Link {...url} className={s.title}>
        {name}
      </Link>
      {/* {days && days?.length >= 5 ? (
        <span className={s.tag}>
          <Day />
        </span>
      ) : (
        <></>
      )} */}
      {/* {product.preco_promocional && product.preco_promocional_ativo && product.preco_promocional >= 120 ? (
        <span className={s.frete_gratis}>FRETE GRÁTIS</span>
      ) : (
        <>
          {price.sell >= 120 && !product.preco_promocional_ativo ? (
            <span className={s.frete_gratis}>FRETE GRÁTIS</span>
          ) : (
            <></>
          )}
        </>
      )} */}
      <BrowserView>
        <div className={s.priceTag}>
          {tipo_produto === 'convencional' && (
            <ProductPrice
              price={price}
              preco_promocional={product.preco_promocional}
              preco_promocional_ativo={product.preco_promocional_ativo}
            />
          )}
          {tipo_produto === 'convencional' ? (
            <div className={s.onlyDesktop}>
              <ProductQuantity onChange={onQuantityChange} quantity={quantity} maximum={stock} dropdown={false} />
            </div>
          ) : (
            <Button href={`${ROUTE_PRODUCT_EXP}${productSlug}`} className={s.euQuero}>
              Eu quero
            </Button>
          )}
        </div>
      </BrowserView>
      {tipo_produto === 'convencional' ? (
        <>
          {stock > 0 ? (
            <>
              <MobileOnlyView>
                <Grid.Row className={s.mobileFooter}>
                  <ProductPrice
                    price={price}
                    preco_promocional={product.preco_promocional || 0}
                    preco_promocional_ativo={product.preco_promocional_ativo}
                  />
                  <ProductQuantity onChange={onQuantityChange} quantity={quantity} maximum={stock} dropdown={false} />
                </Grid.Row>
                <ProductBuy product={freezeProduct} quantity={quantity} className={s.buy} />
              </MobileOnlyView>
              <BrowserView>
                <Grid.Row className={s.footerDesktop}>
                  <div className={s.onlyWide}>
                    <ProductQuantity onChange={onQuantityChange} quantity={quantity} maximum={stock} dropdown={false} />
                  </div>
                  <ProductBuy product={freezeProduct} quantity={quantity} className={s.buy} />
                </Grid.Row>
              </BrowserView>
            </>
          ) : (
            <div className={s.unavailable_text}>Sem estoque</div>
          )}
        </>
      ) : (
        <MobileOnlyView>
          <Button href={`${ROUTE_PRODUCT_EXP}${productSlug}`} className={s.euQuero}>
            Eu quero
          </Button>
        </MobileOnlyView>
      )}
    </div>
  )
}

export default ProductCard
